import React, { useEffect, useState } from 'react'
import { useTranslation } from '@structura/i18n'

interface BeamTemplate {
  id: string
  name: string
  description: string
  materialType: string
  category: string
  spanCount: number
  displayOrder: number
}

interface BeamTemplateGroups {
  [material: string]: BeamTemplate[]
}

interface BeamTemplateSpan {
  length?: number
  e?: number
  a?: number
  i?: number
}

interface BeamTemplateNode {
  supportType: string
  position?: number
}

interface BeamTemplateCrossSection {
  type: string
  height: number
  width: number
}

interface BeamTemplateMaterial {
  materialType: string
  grade?: string
  concreteClass?: string
  timberClass?: string
  elasticModulus?: number
  density: number
}

interface BeamTemplateLoadCase {
  name: string
  category: string
}

interface BeamTemplateLoadCombination {
  name: string
  limitState: string
}

interface BeamTemplateConfiguration {
  spans?: BeamTemplateSpan[]
  nodes?: BeamTemplateNode[]
  crossSection?: BeamTemplateCrossSection
  material?: BeamTemplateMaterial
  loadCases?: BeamTemplateLoadCase[]
  loadCombinations?: BeamTemplateLoadCombination[]
}

interface BeamTemplateDetails extends BeamTemplate {
  configuration: string
  configurationParsed: BeamTemplateConfiguration
}

export interface LibrarySectionProps {
  templateMaterialType: string
  getSessionHeaders: () => HeadersInit
  onError: (message: string | null) => void
}

const LibrarySection: React.FC<LibrarySectionProps> = ({ templateMaterialType, getSessionHeaders, onError }) => {
  const { t } = useTranslation()
  const [beamTemplatesLoading, setBeamTemplatesLoading] = useState<boolean>(false)
  const [beamTemplatesError, setBeamTemplatesError] = useState<string | null>(null)
  const [beamTemplateGroups, setBeamTemplateGroups] = useState<BeamTemplateGroups>({})
  const [selectedBeamTemplate, setSelectedBeamTemplate] = useState<BeamTemplateDetails | null>(null)
  const [beamDetailsLoading, setBeamDetailsLoading] = useState<boolean>(false)

  useEffect(() => {
    const loadBeamTemplates = async (): Promise<void> => {
      setBeamTemplatesLoading(true)
      setBeamTemplatesError(null)
      try {
        const response = await fetch(`/api/BeamTemplates?materialType=${encodeURIComponent(templateMaterialType)}`, {
          headers: getSessionHeaders()
        })

        if (!response.ok) {
          throw new Error(t('admin.library.errLoadHttp', { status: response.status }))
        }

        const templates: BeamTemplate[] = await response.json()
        const groups: BeamTemplateGroups = {}
        for (let index = 0; index < templates.length; index++) {
          const template = templates[index]
          const key = template.materialType.toLowerCase()
          if (!groups[key]) {
            groups[key] = []
          }
          groups[key].push(template)
        }

        setBeamTemplateGroups(groups)
      } catch (err) {
        const message = (err as Error).message || t('admin.library.errLoad')
        setBeamTemplatesError(message)
      } finally {
        setBeamTemplatesLoading(false)
      }
    }

    void loadBeamTemplates()
  }, [templateMaterialType, getSessionHeaders, t])

  const loadBeamTemplateDetails = async (templateId: string): Promise<void> => {
    setBeamDetailsLoading(true)
    try {
      const response = await fetch(`/api/BeamTemplates/${templateId}`, {
        headers: getSessionHeaders()
      })

      if (!response.ok) {
        throw new Error(t('admin.library.errLoadDetailsHttp', { status: response.status }))
      }

      const data: BeamTemplateDetails & { configuration: string } = await response.json()
      const config: BeamTemplateConfiguration = JSON.parse(data.configuration)
      setSelectedBeamTemplate({
        ...data,
        configurationParsed: config
      })
    } catch (err) {
      console.error('Failed to load beam template details:', err)
      onError((err as Error).message)
    } finally {
      setBeamDetailsLoading(false)
    }
  }

  return (
    <>
      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">
            <i className="bi bi-box-seam me-2"></i>
            {t('admin.library.title')}
          </h5>
        </div>
        <div className="card-body">
          <p className="text-muted">
            {t('admin.library.introBefore')} <code>Beam2D.db</code>{t('admin.library.introAfter')}
          </p>

          {beamTemplatesLoading && (
            <div className="d-flex align-items-center mb-3">
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              <span>{t('admin.library.loading')}</span>
            </div>
          )}

          {beamTemplatesError && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {beamTemplatesError}
              <button type="button" className="btn-close" onClick={() => setBeamTemplatesError(null)}></button>
            </div>
          )}

          {!beamTemplatesLoading && !beamTemplatesError && (
            <div className="mt-3">
              <div className="row g-3">
                {['steel', 'concrete', 'timber'].map((materialKey) => {
                  const items = beamTemplateGroups[materialKey] || []
                  if (items.length === 0) {
                    return null
                  }

                  const title =
                    materialKey === 'steel' ? t('admin.library.steelBeams') : materialKey === 'concrete' ? t('admin.library.concreteBeams') : t('admin.library.timberBeams')
                  const icon = materialKey === 'steel' ? 'box' : materialKey === 'concrete' ? 'building' : 'tree'

                  return (
                    <div key={materialKey} className="col-12">
                      <div className="card border-secondary">
                        <div className="card-header bg-light">
                          <h6 className="mb-0">
                            <i className={`bi bi-${icon} me-2`}></i>
                            {title}
                          </h6>
                        </div>
                        <div className="card-body p-0">
                          <div className="table-responsive">
                            <table className="table table-sm table-hover mb-0">
                              <thead>
                                <tr>
                                  <th>{t('admin.library.thName')}</th>
                                  <th>{t('admin.library.thDescription')}</th>
                                  <th className="text-center">{t('admin.library.thCategory')}</th>
                                  <th className="text-center">{t('admin.library.thSpans')}</th>
                                  <th className="text-end">{t('admin.library.thOrderMaterial')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {items.map((template) => (
                                  <tr key={template.id} style={{ cursor: 'pointer' }}>
                                    <td onClick={() => void loadBeamTemplateDetails(template.id)}>
                                      <strong className="text-primary">
                                        <i className="bi bi-info-circle me-1"></i>
                                        {template.name}
                                      </strong>
                                    </td>
                                    <td className="text-muted small">{template.description}</td>
                                    <td className="text-center">
                                      <span className="badge bg-info-subtle text-info text-capitalize">
                                        {template.category.replace('-', ' ')}
                                      </span>
                                    </td>
                                    <td className="text-center">
                                      <span className="badge bg-secondary-subtle text-secondary">
                                        {template.spanCount} {t('admin.library.spanUnit')}
                                      </span>
                                    </td>
                                    <td className="text-end">{template.displayOrder}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}

                {Object.keys(beamTemplateGroups).length === 0 && (
                  <div className="col-12">
                    <div className="alert alert-info mb-0">
                      <i className="bi bi-info-circle me-2"></i>
                      {t('admin.library.noTemplatesBefore')} <code>Beam2D.db</code>{t('admin.library.noTemplatesAfter')}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Beam Template Details Modal */}
      {selectedBeamTemplate && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setSelectedBeamTemplate(null)}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-box-seam me-2"></i>
                  {selectedBeamTemplate.name}
                </h5>
                <button type="button" className="btn-close" onClick={() => setSelectedBeamTemplate(null)}></button>
              </div>
              <div className="modal-body">
                {beamDetailsLoading ? (
                  <div className="text-center py-4">
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    {t('admin.library.loadingDetails')}
                  </div>
                ) : (
                  <>
                    <div className="row mb-3">
                      <div className="col-md-8">
                        <p className="text-muted">{selectedBeamTemplate.description}</p>
                      </div>
                      <div className="col-md-4 text-end">
                        <span className="badge bg-primary me-2 text-capitalize">{selectedBeamTemplate.materialType}</span>
                        <span className="badge bg-info text-capitalize">{selectedBeamTemplate.category?.replace('-', ' ')}</span>
                      </div>
                    </div>

                    <ul className="nav nav-tabs mb-3">
                      <li className="nav-item">
                        <a className="nav-link active" data-bs-toggle="tab" href="#geometry-tab">
                          <i className="bi bi-rulers me-1"></i>
                          {t('admin.library.tabGeometry')}
                        </a>
                      </li>
                      <li className="nav-item">
                        <a className="nav-link" data-bs-toggle="tab" href="#material-tab">
                          <i className="bi bi-box me-1"></i>
                          {t('admin.library.tabMaterial')}
                        </a>
                      </li>
                      <li className="nav-item">
                        <a className="nav-link" data-bs-toggle="tab" href="#loads-tab">
                          <i className="bi bi-arrow-down-up me-1"></i>
                          {t('admin.library.tabLoads')}
                        </a>
                      </li>
                      <li className="nav-item">
                        <a className="nav-link" data-bs-toggle="tab" href="#config-tab">
                          <i className="bi bi-code-square me-1"></i>
                          {t('admin.library.tabFullConfig')}
                        </a>
                      </li>
                    </ul>

                    <div className="tab-content">
                      {/* Geometry Tab */}
                      <div className="tab-pane fade show active" id="geometry-tab">
                        <h6 className="mb-3">{t('admin.library.spansAndNodes')}</h6>
                        {selectedBeamTemplate.configurationParsed?.spans && (
                          <table className="table table-sm">
                            <thead>
                              <tr>
                                <th>{t('admin.library.thSpan')}</th>
                                <th className="text-end">{t('admin.library.thLengthM')}</th>
                                <th className="text-end">E (Pa)</th>
                                <th className="text-end">A (m²)</th>
                                <th className="text-end">I (m⁴)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedBeamTemplate.configurationParsed.spans.map((span: BeamTemplateSpan, idx: number) => (
                                <tr key={idx}>
                                  <td>{t('admin.library.spanRow', { n: idx + 1 })}</td>
                                  <td className="text-end">{span.length?.toFixed(2)}</td>
                                  <td className="text-end">{span.e?.toExponential(2)}</td>
                                  <td className="text-end">{span.a?.toFixed(5)}</td>
                                  <td className="text-end">{span.i?.toExponential(3)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}

                        {selectedBeamTemplate.configurationParsed?.nodes && (
                          <>
                            <h6 className="mb-3 mt-4">{t('beam.geometry.supportConditions')}</h6>
                            <table className="table table-sm">
                              <thead>
                                <tr>
                                  <th>{t('admin.library.thNode')}</th>
                                  <th>{t('admin.library.thSupportType')}</th>
                                  <th className="text-end">{t('admin.library.thPositionM')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedBeamTemplate.configurationParsed.nodes.map((node: BeamTemplateNode, idx: number) => (
                                  <tr key={idx}>
                                    <td>{t('admin.library.nodeRow', { n: idx })}</td>
                                    <td><span className="badge bg-secondary">{node.supportType}</span></td>
                                    <td className="text-end">{node.position?.toFixed(2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </>
                        )}

                        {selectedBeamTemplate.configurationParsed?.crossSection && (
                          <>
                            <h6 className="mb-3 mt-4">{t('beam.geometry.crossSection')}</h6>
                            <div className="row g-2">
                              <div className="col-md-6">
                                <small className="text-muted">{t('admin.library.typeLabel')}</small>
                                <div><strong className="text-capitalize">{selectedBeamTemplate.configurationParsed.crossSection.type}</strong></div>
                              </div>
                              <div className="col-md-3">
                                <small className="text-muted">{t('admin.library.heightLabel')}</small>
                                <div><strong>{selectedBeamTemplate.configurationParsed.crossSection.height} mm</strong></div>
                              </div>
                              <div className="col-md-3">
                                <small className="text-muted">{t('admin.library.widthLabel')}</small>
                                <div><strong>{selectedBeamTemplate.configurationParsed.crossSection.width} mm</strong></div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Material Tab */}
                      <div className="tab-pane fade" id="material-tab">
                        {selectedBeamTemplate.configurationParsed?.material && (
                          <div className="row g-3">
                            <div className="col-md-6">
                              <label className="small text-muted">{t('admin.library.materialType')}</label>
                              <div className="fw-bold text-capitalize">{selectedBeamTemplate.configurationParsed.material.materialType}</div>
                            </div>
                            {selectedBeamTemplate.configurationParsed.material.grade && (
                              <div className="col-md-6">
                                <label className="small text-muted">{t('admin.library.grade')}</label>
                                <div className="fw-bold">{selectedBeamTemplate.configurationParsed.material.grade}</div>
                              </div>
                            )}
                            {selectedBeamTemplate.configurationParsed.material.concreteClass && (
                              <div className="col-md-6">
                                <label className="small text-muted">{t('admin.library.concreteClass')}</label>
                                <div className="fw-bold">{selectedBeamTemplate.configurationParsed.material.concreteClass}</div>
                              </div>
                            )}
                            {selectedBeamTemplate.configurationParsed.material.timberClass && (
                              <div className="col-md-6">
                                <label className="small text-muted">{t('admin.library.timberClass')}</label>
                                <div className="fw-bold">{selectedBeamTemplate.configurationParsed.material.timberClass}</div>
                              </div>
                            )}
                            <div className="col-md-6">
                              <label className="small text-muted">{t('admin.library.elasticModulus')}</label>
                              <div className="fw-bold">{selectedBeamTemplate.configurationParsed.material.elasticModulus?.toExponential(2)} Pa</div>
                            </div>
                            <div className="col-md-6">
                              <label className="small text-muted">{t('admin.library.density')}</label>
                              <div className="fw-bold">{selectedBeamTemplate.configurationParsed.material.density} kg/m³</div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Loads Tab */}
                      <div className="tab-pane fade" id="loads-tab">
                        {selectedBeamTemplate.configurationParsed?.loadCases && (
                          <>
                            <h6 className="mb-3">{t('admin.library.loadCases')}</h6>
                            <ul className="list-group mb-4">
                              {selectedBeamTemplate.configurationParsed.loadCases.map((lc: BeamTemplateLoadCase, idx: number) => (
                                <li key={idx} className="list-group-item">
                                  <strong>{lc.name}</strong>
                                  <span className="badge bg-info ms-2">{lc.category}</span>
                                </li>
                              ))}
                            </ul>
                          </>
                        )}

                        {selectedBeamTemplate.configurationParsed?.loadCombinations && (
                          <>
                            <h6 className="mb-3">{t('admin.library.loadCombinations')}</h6>
                            {selectedBeamTemplate.configurationParsed.loadCombinations.map((combo: BeamTemplateLoadCombination, idx: number) => (
                              <div key={idx} className="card mb-2">
                                <div className="card-body p-2">
                                  <div className="fw-bold">{combo.name}</div>
                                  <small className="text-muted">{combo.limitState}</small>
                                </div>
                              </div>
                            ))}
                          </>
                        )}
                      </div>

                      {/* Full Config Tab */}
                      <div className="tab-pane fade" id="config-tab">
                        <pre className="bg-light p-3 rounded" style={{ maxHeight: '500px', overflow: 'auto', fontSize: '0.85rem' }}>
                          {JSON.stringify(selectedBeamTemplate.configurationParsed, null, 2)}
                        </pre>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setSelectedBeamTemplate(null)}>
                  {t('beam.common.close')}
                </button>
                <button type="button" className="btn btn-primary" disabled>
                  <i className="bi bi-pencil me-1"></i>
                  {t('admin.common.editComingSoon')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default LibrarySection
